import { useRef } from "react";
import { ArrowRight, Radar, Timer, Layers, Zap } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { KineticWords } from "./KineticWords";

const ANGLES = [-90, -18, 54, 126, 198];
const VALUES = [3.4, 2.2, 3.8, 1.6, 2.9];

const pt = (angle, value, c = 110, r = 92) => {
  const rad = (angle * Math.PI) / 180;
  const rr = (value / 4) * r;
  return [c + rr * Math.cos(rad), c + rr * Math.sin(rad)];
};

const chipIcons = [Timer, Layers, Zap];

export const ZatPromo = () => {
  const { t, lang } = useLanguage();
  const z = t.zat;
  const points = ANGLES.map((a, i) => pt(a, VALUES[i]).join(",")).join(" ");

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-9, 9]);
  const yRadar = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section ref={ref} data-testid="zat-promo" className="relative overflow-hidden py-24 sm:py-32 lg:py-40">
      <div className="pointer-events-none absolute -left-32 top-0 h-[420px] w-[520px] rounded-full blur-[140px]" style={{ background: "radial-gradient(circle, rgba(0,245,212,0.10) 0%, transparent 70%)" }} aria-hidden="true" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[460px] rounded-full blur-[140px]" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 65%)" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "12%", top: "30%", width: 3, height: 3, animationDelay: "0.5s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "85%", top: "22%", width: 2, height: 2, animationDelay: "1.6s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "70%", top: "78%", width: 3, height: 3, animationDelay: "2.4s" }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12">
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-teal-400">{z.kicker[lang]}</p>
        <h2
          data-testid="zat-heading"
          className="font-display mt-6 max-w-6xl text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl"
        >
          <KineticWords key={lang} text={z.heading[lang]} />
        </h2>

        <div className="mt-14 grid items-center gap-14 lg:grid-cols-[1fr_440px]">
          <div>
            <p className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{z.sub[lang]}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {z.chips.map((c, i) => {
                const Icon = chipIcons[i];
                return (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.6, rotate: -6 }}
                    whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.2 + i * 0.12 }}
                    className="flex items-center gap-2 rounded-full border border-teal-400/25 bg-teal-400/10 px-4 py-1.5 text-xs font-semibold text-teal-200"
                  >
                    <Icon size={13} /> {c[lang]}
                  </motion.span>
                );
              })}
            </div>
            <motion.a
              href="/matrix"
              data-testid="zat-cta-button"
              whileHover={{ scale: 1.06, rotate: -1 }}
              whileTap={{ scale: 0.94 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-teal-500 px-9 py-4 text-base font-black uppercase tracking-widest text-[#052E2B] shadow-[0_0_30px_rgba(0,245,212,0.25)] transition-colors duration-200 hover:bg-[#00F5D4] hover:shadow-[0_0_50px_rgba(0,245,212,0.5)]"
            >
              {z.cta[lang]}
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1.5" />
            </motion.a>
            <p className="mt-4 font-mono text-[11px] tracking-wider text-slate-500">{z.note[lang]}</p>
          </div>

          <motion.div
            style={{ rotate, y: yRadar }}
            className="relative mx-auto w-full max-w-sm lg:max-w-none"
            data-testid="zat-radar"
          >
            <div className="spin-slow pointer-events-none absolute -inset-6 rounded-full border-2 border-dashed border-teal-400/25" aria-hidden="true" />
            <div
              className="wiggle absolute -right-4 -top-6 z-10 rounded-full border-2 border-teal-300 bg-teal-400 px-5 py-2 font-mono text-[11px] font-black uppercase tracking-widest text-[#052E2B] shadow-[0_0_30px_rgba(0,245,212,0.5)]"
              aria-hidden="true"
            >
              {lang === "cs" ? "Zdarma · 60 s" : "Free · 60 s"}
            </div>
            <svg viewBox="0 0 220 220" className="w-full">
              {[0.25, 0.5, 0.75, 1].map((r) => (
                <polygon key={r} points={ANGLES.map((a) => pt(a, r * 4).join(",")).join(" ")} fill="none" stroke="rgba(45,212,191,0.15)" strokeWidth="1" />
              ))}
              {ANGLES.map((a) => {
                const [x, y] = pt(a, 4);
                return <line key={a} x1="110" y1="110" x2={x} y2={y} stroke="rgba(45,212,191,0.15)" strokeWidth="1" />;
              })}
              <polygon points={points} fill="rgba(45,212,191,0.15)" stroke="#2DD4BF" strokeWidth="2" style={{ filter: "drop-shadow(0 0 12px rgba(45,212,191,0.5))" }}>
                <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
              </polygon>
              {ANGLES.map((a, i) => {
                const [x, y] = pt(a, VALUES[i]);
                return <circle key={i} cx={x} cy={y} r="3.5" fill="#2DD4BF" />;
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <Radar size={26} className="text-teal-300/80" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
